import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@hero/web-lib';
import { type Contact, getObjectId, type Id } from '@hero/schema';
import { ChooseLocationModal, type ChosenLocation } from '../../../app/Map';
import { ContactSelector } from '../../Contact/Selector/ContactSelector';
import {
  CardField,
  CardFieldShell,
  CardLocationField,
  CardTextField,
  ClientRefField,
  EntityRefField,
  cardControlClass,
} from './CardFields';
import { useAgentChatStore } from './store';
import { useReportCardBusy } from './useCardBusy';

/**
 * Body for `create_saved_location` / `update_saved_location`.
 *
 * Everything the agent staged is editable in place, because a saved
 * place is exactly the kind of record the user wants to fix *before*
 * it exists: the name the agent guessed from a chat message, and —
 * far more often — the pin, which the agent had to infer from a
 * landmark or a city name.
 *
 * The pin is chosen through {@link ChooseLocationModal} (search bar,
 * current location, draggable marker) and displayed as a place name.
 * Coordinates are never printed: `{"lat":24.15,"lng":47.32}` tells
 * the user nothing about whether the pin is right, which is the only
 * question this card exists to answer.
 */
export const SavedLocationPayloadPreview = ({
  confirmationId,
  payload,
}: {
  confirmationId: string;
  payload: Record<string, unknown>;
}) => {
  const { t } = useTranslation('m-agent');
  const updatePayload = useAgentChatStore(s => s.updatePendingConfirmationPayload);
  const [isPickerOpen, setPickerOpen] = useState(false);

  // The picker owns the pin until the user saves it, so the card can't
  // be confirmed (from here or from the stack header) while it's open.
  useReportCardBusy(confirmationId, 'location', isPickerOpen);

  const name = readEditableString(payload, 'name');
  const notes = readString(payload, 'notes') ?? '';
  const clientId = readString(payload, 'clientId');
  const clientName = readString(payload, 'clientName');
  const savedLocationId = readString(payload, '_id');
  const point = readPoint(payload.location);
  const isAccurate = payload.isAccurate === true;
  // Display-only labels the staging tool attaches (and the picker
  // refreshes) so the row can say "Al Kharj industrial city" instead
  // of a coordinate pair. Stripped server-side before persisting.
  const locationLabel = readString(payload, 'locationLabel');
  const locationAddress = readString(payload, 'locationAddress');
  // The user pasted a Maps link that would not unfurl, so the pin is a
  // city-level stand-in. Saying so on the card is the whole point: the
  // place still saves, and the one person who can fix the pin is looking
  // right at it.
  const isLinkUnresolved = payload.locationUrlUnresolved === true;
  const contacts = readContactRefs(payload.contacts);

  const handleContactsChange = (next: Contact[] | undefined) => {
    updatePayload(confirmationId, {
      contacts: (next ?? []).map(c => ({ _id: c._id })),
    });
  };

  const handleChoose = (chosen: ChosenLocation) => {
    updatePayload(confirmationId, {
      location: { lat: chosen.lat, lng: chosen.lng },
      isAccurate: chosen.isAccurate ?? false,
      locationUrl: chosen.locationUrl,
      locationLabel: chosen.name,
      locationAddress: chosen.fullAddress,
      locationUrlUnresolved: false,
      // The agent's guessed name stands unless it never had one.
      ...(name.trim() ? {} : { name: chosen.name ?? '' }),
    });
  };

  return (
    <>
      <div className='space-y-2.5 rounded-xl border border-slate-100 bg-slate-50/50 px-3 py-2.5 text-xs'>
        {/* Who the place belongs to comes first: it frames every field
            under it (which contacts are offered, whose yard this is). */}
        {clientId ? (
          <ClientRefField
            label={t('confirmation.fields.client_name', { defaultValue: 'Client' })}
            clientId={clientId}
            fallbackName={clientName}
          />
        ) : (
          <CardField
            label={t('confirmation.fields.client_name', { defaultValue: 'Client' })}
            value={t('confirmation.location.own_branch', { defaultValue: 'Your own branch' })}
          />
        )}

        <CardTextField
          label={t('confirmation.fields.name', { defaultValue: 'Place name' })}
          value={name}
          required
          placeholder={t('confirmation.fields.name_placeholder', {
            defaultValue: 'e.g. Riyadh warehouse — Bay 4',
          })}
          onChange={next => updatePayload(confirmationId, { name: next })}
        />

        <CardLocationField
          label={t('confirmation.location.label', { defaultValue: 'Location' })}
          placeLabel={locationLabel}
          addressLabel={locationAddress}
          isAccurate={isAccurate}
          hasPin={!!point}
          required
          onEdit={() => setPickerOpen(true)}
        />

        {isLinkUnresolved ? (
          <p className='rounded-lg bg-amber-50 px-2.5 py-1.5 text-[11px] leading-relaxed text-amber-800'>
            {t('confirmation.location.link_unresolved', {
              defaultValue:
                "We couldn't read the exact pin from the link, so the city is used. Tap the location to adjust it.",
            })}
          </p>
        ) : null}

        {/* Same picker as the dashboard form, so a place staged from chat
            can get its gate contact without leaving the conversation.
            Scoped to the client when there is one; own-branch places see
            the org's contacts. Required: a yard nobody can call is a
            record dispatch can't use. */}
        <CardFieldShell
          label={t('confirmation.fields.contact', { defaultValue: 'Contact' })}
          required
          hint={
            contacts.length === 0 ? (
              <span className='text-[11px] text-amber-700'>
                {t('confirmation.fields.contact_required', {
                  defaultValue: 'Pick who to call at this place, or create a new contact.',
                })}
              </span>
            ) : null
          }>
          <ContactSelector
            customerId={clientId ? getObjectId(clientId) : undefined}
            selectedContacts={contacts}
            onContactsChange={handleContactsChange}
            onContactSave={contact =>
              updatePayload(confirmationId, {
                contacts: [{ _id: contact._id }, ...contacts],
              })
            }
            className={cn(cardControlClass, 'h-auto py-1')}
          />
        </CardFieldShell>

        {savedLocationId ? (
          <EntityRefField
            label={t('confirmation.location.updating', { defaultValue: 'Updating' })}
            kind='saved_location'
            entityId={savedLocationId}
            fallbackLabel={
              name.trim() ||
              t('confirmation.location.saved_place', { defaultValue: 'Saved place' })
            }
          />
        ) : null}

        {/* Read-only: `SavedLocation` has no notes field, so this is
            conversation context the agent captured, not something the
            confirm will persist. An input here would lie. */}
        {notes ? (
          <CardField
            label={t('confirmation.fields.notes', { defaultValue: 'Notes' })}
            value={notes}
          />
        ) : null}
      </div>

      <ChooseLocationModal
        open={isPickerOpen}
        onOpenChange={setPickerOpen}
        title={t('confirmation.location.picker_title', { defaultValue: 'Where is this place?' })}
        clientId={clientId}
        value={
          point
            ? {
                lat: point.lat,
                lng: point.lng,
                name: locationLabel,
                fullAddress: locationAddress,
                isAccurate,
              }
            : null
        }
        onChoose={handleChoose}
      />
    </>
  );
};

const readString = (payload: Record<string, unknown>, key: string): string | undefined => {
  const v = payload[key];
  return typeof v === 'string' && v.trim() ? v.trim() : undefined;
};

/**
 * Raw read for a value that feeds a text input. `readString` trims, which
 * is right for a label but fatal for a controlled input: the trimmed value
 * flows straight back as `value`, so a space typed at the end of the name
 * is erased on the next render and the key looks dead.
 */
const readEditableString = (payload: Record<string, unknown>, key: string): string =>
  typeof payload[key] === 'string' ? (payload[key] as string) : '';

/**
 * Staged contacts come back from Mongo as `[{ _id: '<hex>' }]`, so the ids
 * are re-hydrated for the selector, which resolves them through
 * `useContactApi`.
 */
const readContactRefs = (value: unknown): { _id: Id }[] => {
  if (!Array.isArray(value)) return [];
  return value.flatMap(entry => {
    const raw = (entry as { _id?: unknown })?._id;
    if (raw == null) return [];
    const id = typeof raw === 'string' ? raw : String(raw);
    return id.trim() ? [{ _id: getObjectId(id) }] : [];
  });
};

/** `{ lat, lng }` survives the Mongo round-trip untyped. */
const readPoint = (value: unknown): { lat: number; lng: number } | undefined => {
  if (typeof value !== 'object' || value === null) return undefined;
  const p = value as { lat?: unknown; lng?: unknown };
  if (typeof p.lat !== 'number' || typeof p.lng !== 'number') return undefined;
  if (p.lat === 0 && p.lng === 0) return undefined;
  return { lat: p.lat, lng: p.lng };
};
